import useGetAuthData from "@/hooks/useGetAuthData";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";

const logoutEscapeVariants = cva(
    "inline-flex items-center gap-2 text-sm font-medium transition-colors",
    {
        variants: {
            variant: {
                link: "underline-offset-4 hover:underline",
                pill: "rounded-full border border-white/30 bg-white/15 px-5 py-2.5 text-white shadow-sm backdrop-blur-md hover:bg-white/25",
                pillLight:
                    "rounded-full border border-neutral-lighter bg-white/70 px-5 py-2.5 text-neutral-darker shadow-sm backdrop-blur-sm hover:bg-white",
            },
        },
        defaultVariants: {
            variant: "link",
        },
    },
);

type LogoutEscapeLinkProps = {
    className?: string;
    label?: string;
    variant?: VariantProps<typeof logoutEscapeVariants>["variant"] | "outline";
};

const LogoutEscapeLink = ({
    className,
    label,
    variant = "link",
}: LogoutEscapeLinkProps) => {
    const { t } = useTranslation();
    const { handleLogout } = useGetAuthData();
    const resolvedLabel = label ?? t("settings.account.disconnect");

    if (variant === "outline") {
        return (
            <Button
                className={cn("w-full", className)}
                icon={<LogOut className="h-4 w-4" />}
                onClick={handleLogout}
                size="lg"
                variant="outline"
            >
                {resolvedLabel}
            </Button>
        );
    }

    const showIcon = variant !== "link";

    return (
        <button
            className={cn(logoutEscapeVariants({ variant }), className)}
            onClick={handleLogout}
            type="button"
        >
            {showIcon && <LogOut className="h-4 w-4" strokeWidth={1.75} />}
            {resolvedLabel}
        </button>
    );
};

export default LogoutEscapeLink;
